import React, { useRef, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { getData, Postdata } from "../../API/GlobalApi";
import Notifier from "../../Utils/notify";

<style>
    {`
@keyframes skeleton-loading {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}
`}
</style>
const StudentSelfAdmitCardGenerations = () => {
    const [searchParams] = useSearchParams();
    const studentId = searchParams.get("student_id");
    const activityId = searchParams.get("activity_id");
    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const otpInputs = useRef([]);
    const [resendTimer, setResendTimer] = useState(0);
    const [studentData, setStudentData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activityMeta, setActivityMeta] = useState(null);

    const getStudentDetails = async () => {
        try {
            setLoading(true);
            const res = await getData(
                `academics/activities/check-prerequisites-for-admit-card/?student_id=${studentId}&activity_id=${activityId}`
            );

            setStudentData(res?.prerequisite_status);
            setOtpSent(res?.otp_sent || false);
            setOtpVerified(res?.otp_verified || false);
            setActivityMeta({
                otp_sent: res?.otp_sent,
                otp_verified: res?.otp_verified,
                otp_expired: res?.otp_expired,
                convenience_fee_value: res?.convenience_fee_value,
                activity_name: res?.activity_name,
                display_download_admit_card_button: res?.display_download_admit_card_button,
            });

        } catch (error) {
            console.error("Error fetching student details", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (studentId && activityId) {
            getStudentDetails();
        }
    }, [studentId, activityId]);

    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setInterval(() => {
                setResendTimer(prev => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [resendTimer]);

    const sendOtp = async () => {
        const payload = {
            student_id: studentId,
            activity_id: activityId,
        }
        const res = await Postdata('academics/activities/send-otp-for-admit-card/', payload)

        if (!res.error) {
            Notifier.success(res?.message)
            setOtpSent(true);
            setResendTimer(30);
            setTimeout(() => otpInputs.current[0]?.focus(), 100);
        }
        Notifier.error(res?.error)


    };

    const handleOtpChange = (e, index) => {
        const value = e.target.value;
        if (!/^[0-9]?$/.test(value)) return;

        const updatedOtp = [...otp];
        updatedOtp[index] = value;
        setOtp(updatedOtp);

        if (value && index < 5) otpInputs.current[index + 1]?.focus();
        if (!value && index > 0) otpInputs.current[index - 1]?.focus();
    };

    const verifyOTP = async () => {
        const enteredOtp = otp.join("");

        if (enteredOtp.length !== 6) return;

        const payload = {
            student_id: studentId,
            otp: enteredOtp,
            activity_id: activityId,
        };

        try {
            const res = await Postdata(
                "academics/activities/verify-otp-for-admit-card/",
                payload
            );
            console.log("res", res)
            if (res?.error) {
                Notifier.error(res.error)
                return;
            }
            Notifier.success(res?.message);
            setOtpVerified(true);
            getStudentDetails();
        } catch (error) {
            console.error("OTP verification failed", error);
        } finally {
            // optional: cleanup / logging
            // setVerifying(false);
        }
    };
    const NO_DUES_AMOUNT = Math.abs(Number(studentData?.ledger_balance || 0));
    const CONVENIENCE_CHARGE = Number(activityMeta?.convenience_fee_value || 0);
    const TOTAL_AMOUNT = NO_DUES_AMOUNT + CONVENIENCE_CHARGE;
    const ledgerBalance = Number(studentData?.ledger_balance || 0);
    const convenienceFee = Number(activityMeta?.convenience_fee_value || 0);


    // Total amount student needs to pay
    const totalAmount = ledgerBalance + convenienceFee;

    // Payment required if totalAmount > 0
    // const isPaymentRequired = totalAmount > 0;


    // Paid if ledger balance is zero or positive
    const isPaymentCleared = ledgerBalance >= 0;

    // Pay now if ledger balance is negative
    const isPaymentRequired = ledgerBalance < 0;


    const allPrechecksCompleted =
        studentData?.all_precheck_names?.length > 0 &&
        studentData?.all_precheck_names?.every(name =>
            studentData?.completed_precheck_names?.includes(name)
        );
    console.log("isPaymentRequired", isPaymentRequired)
    // const isPaymentRequired = convenienceFee === 0;
    // const isPaymentCleared = totalAmount >= 0;
    const canDownloadAdmitCard =
        convenienceFee === 0 && ledgerBalance >= 0 && allPrechecksCompleted 



    // SECURITY: the raw PDF URL is no longer returned by the backend. The
    // download now goes through `secure-download-admit-card/`, which re-runs
    // every gate server-side and streams the file directly.
    const admitCard = studentData?.admit_cards_list?.[0];


    const sanitize = (text = "") =>
        text
            .toString()
            .trim()
            .replace(/\s+/g, "_")
            .replace(/[^a-zA-Z0-9_-]/g, "")
            .toUpperCase();

    const [isDownloading, setIsDownloading] = useState(false);
    const handleDownloadAdmitCard = async () => {
        if (isDownloading) return; // ⛔ prevent multiple hits
        try {
            setIsDownloading(true);

            const studentName = sanitize(studentData?.full_name);
            const enrollNumber = sanitize(admitCard?.enroll_number);
            const examYear = sanitize(admitCard?.examination_year);
            const fileName =
                enrollNumber || examYear
                    ? `ADMIT_CARD_${studentName}_${enrollNumber}_${examYear}.pdf`
                    : `ADMIT_CARD_${studentName || 'student'}.pdf`;

            // Hit the gated streaming endpoint; the server re-checks OTP,
            // prereqs, payment, and the reconciliation gate before sending bytes.
            const apiBase = `${process.env.REACT_APP_API_BASE_URL}api/v1/`;
            const response = await fetch(
                `${apiBase}academics/activities/secure-download-admit-card/`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        student_id: studentId,
                        activity_id: activityId,
                    }),
                },
            );

            if (!response.ok) {
                let message = `Download failed (HTTP ${response.status})`;
                try {
                    const data = await response.json();
                    if (data?.error) message = data.error;
                } catch (_) { /* non-JSON error body */ }
                Notifier.error(message);
                return;
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Admit card download failed", error);
            Notifier.error("Unable to download admit card");
        } finally {
            setIsDownloading(false); // ✅ always reset
        }
    };
    const [isPaying, setIsPaying] = useState(false);


    const handlePayFee = async () => {
        try {
            setIsPaying(true);
            const payload = {
                student_id: studentId,
                activity_id: activityId,
                amount: TOTAL_AMOUNT,
            };

            const res = await Postdata(
                "academics/activities/admit-card-payment-gateway/",
                payload
            );

            console.log("resin payment", res);

            if (res?.payment_url) {
                window.location.href = res.payment_url; // ✅ redirect
            }
        } catch (error) {
            console.error("Payment initiation failed", error);
            setIsPaying(false); // reset only on error
        }
    };




    const SkeletonBox = ({ height = 14, width = "100%", radius = 6, mt = 0 }) => (
        <div
            style={{
                height,
                width,
                background: "linear-gradient(90deg, #f0f0f0 25%, #e4e4e4 37%, #f0f0f0 63%)",
                backgroundSize: "400% 100%",
                animation: "skeleton-loading 1.4s ease infinite",
                borderRadius: radius,
                marginTop: mt,
            }}
        />
    );



    if (loading) {
        return (
            <div style={{ maxWidth: "420px", margin: "auto", padding: "16px" }}>
                {/* Student Name */}
                <SkeletonBox height={22} width="70%" />
                <SkeletonBox height={14} width="50%" mt={8} />
                <SkeletonBox height={14} width="60%" mt={6} />
                <div
                    style={{
                        border: "1.5px dashed #7b61ff",
                        borderRadius: "10px",
                        padding: "14px",
                        marginTop: "14px",
                    }}
                >
                    <SkeletonBox height={14} width="80%" />
                    <SkeletonBox height={32} width="120px" radius={20} mt={12} />
                </div>
                <div
                    style={{
                        border: "1.5px dashed #7b61ff",
                        borderRadius: "10px",
                        padding: "14px",
                        marginTop: "18px",
                    }}   >
                    {[1, 2].map(i => (
                        <div
                            key={i}
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginBottom: "12px",
                                alignItems: "center",
                            }}
                        >
                            <SkeletonBox width="55%" />
                            <SkeletonBox width="80px" height={22} radius={20} />
                        </div>
                    ))}
                </div>
                <div
                    style={{
                        border: "1.5px dashed #7b61ff",
                        borderRadius: "10px",
                        padding: "14px",
                        marginTop: "18px",
                    }}
                >
                    <SkeletonBox width="100%" />
                    <SkeletonBox width="100%" mt={8} />
                    <SkeletonBox width="60%" mt={12} />
                </div>
            </div>
        );
    }


    return (
        <div style={{ maxWidth: "420px", margin: "auto", padding: "16px" }}>
            <h3 className="text-center">{studentData?.full_name}</h3>

            <p style={{ fontSize: "14px", color: "#6c757d", textAlign: "center" }}>
                BITE Reg No: {studentData?.permanent_registration_number}
            </p>
            <p style={{ fontSize: "14px", color: "#6c757d", textAlign: "center" }}>
                {studentData?.course_name}
            </p>
            <div
                style={{
                    border: "1.5px dashed #7b61ff",
                    borderRadius: "10px",
                    padding: "14px",
                    marginTop: "14px",
                    display: "flex",
                    flexDirection: otpVerified ? "row" : otpSent ? "column" : "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "10px",
                }}
            >
                <p>
                    Mobile Number: <b>{studentData?.mobile}</b>
                </p>

                {!otpSent && !otpVerified && (
                    <button
                        onClick={sendOtp}
                        style={{
                            background: "#e9fdf0",
                            color: "#28a745",
                            border: "1px solid #baf0cc",
                            borderRadius: "20px",
                            padding: "4px 20px",
                            cursor: "pointer",
                        }}
                    >
                        Send OTP
                    </button>
                )}

                {otpSent && !otpVerified && (
                    <>
                        <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={el => (otpInputs.current[index] = el)}
                                    value={digit}
                                    maxLength={1}
                                    onChange={e => handleOtpChange(e, index)}
                                    onKeyDown={e => e.key === "Enter" && verifyOTP()}
                                    type="text"              // keep text (important for iOS)
                                    inputMode="numeric"      // ⭐ opens numeric keypad
                                    pattern="[0-9]*"         // ⭐ hints digits only
                                    autoComplete="one-time-code" // ⭐ iOS OTP autofill
                                    style={{
                                        width: "32px",
                                        height: "32px",
                                        textAlign: "center",
                                        borderRadius: "6px",
                                        border: "1px solid #ccc",
                                    }}
                                />
                            ))}
                        </div>

                        <button
                            onClick={verifyOTP}
                            style={{
                                background: "#e9fdf0",
                                color: "#28a745",
                                border: "1px solid #baf0cc",
                                borderRadius: "20px",
                                padding: "4px 20px",
                                cursor: "pointer",
                                marginTop: "12px",
                            }}
                        >
                            Verify OTP
                        </button>

                        <button
                            onClick={sendOtp}
                            disabled={resendTimer > 0}
                            style={{
                                background: "none",
                                border: "none",
                                color: resendTimer > 0 ? "#6c757d" : "#7F56DA",
                                cursor: resendTimer > 0 ? "not-allowed" : "pointer",
                                marginTop: "8px",
                                textDecoration: resendTimer > 0 ? ' ' : "underline"
                            }}
                        >
                            Resend OTP {resendTimer > 0 && `(${resendTimer}s)`}
                        </button>
                    </>
                )}

                {otpVerified && (
                    <span className="sd-p"
                        style={{
                            background: "#e9fdf0",
                            color: "#28a745",
                            padding: "4px 12px",
                            borderRadius: "20px",
                            fontSize: "13px",
                            border: "1px solid #baf0cc",
                        }}
                    >
                        Completed
                    </span>
                )}
            </div>

            {otpVerified && (
                <>
                    <div
                        style={{
                            border: "1.5px dashed #7b61ff",
                            borderRadius: "10px",
                            padding: "14px",
                            marginTop: "18px",
                        }}
                    >
                        {studentData?.all_precheck_names?.map((name, index) => {
                            const isCompleted =
                                studentData?.completed_precheck_names?.includes(name);
                            const isNoDues = name === "No Dues Clearance";

                            return (
                                <div
                                    key={index}
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        marginBottom: "10px",
                                        alignItems: "center",
                                    }}
                                >
                                    <div>
                                        {name}

                                        {isNoDues && !isCompleted && (
                                            <div style={{ fontSize: "12px", color: "#dc3545" }}>
                                                Due Amount: ₹ {NO_DUES_AMOUNT.toFixed(2)}
                                            </div>
                                        )}
                                    </div>

                                    <span
                                        className="sd-p"
                                        style={{
                                            background: isCompleted ? "#e9fdf0" : "#fee2e2",
                                            color: isCompleted ? "#28a745" : "#991b1b",
                                            borderRadius: "20px",
                                            fontSize: "13px",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            padding: "4px 15px",
                                            border: isCompleted ? "1px solid #baf0cc" : " ",
                                        }}
                                    >
                                        {isCompleted ? "Completed" : "Pending"}
                                    </span>
                                </div>
                            );
                        })}
                    </div>


                    <div
                        style={{
                            border: "1.5px dashed #7b61ff",
                            borderRadius: "10px",
                            padding: "14px",
                            marginTop: "18px",
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>No Dues Clearance Amount</span>
                            <span>₹ {NO_DUES_AMOUNT.toFixed(2)}</span>
                        </div>

                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px" }}>
                            <span>Convenience Charge</span>
                            <span>₹ {CONVENIENCE_CHARGE.toFixed(2)}</span>
                        </div>

                        <hr />

                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <b>Total Amount</b>
                            <b>₹ {TOTAL_AMOUNT.toFixed(2)}</b>
                        </div>

                        <div style={{ textAlign: "center", marginTop: "14px" }}>
                            <button
                                disabled={isPaymentCleared || isPaying}
                                onClick={handlePayFee}
                                style={{
                                    background: "#e9fdf0",
                                    color: "#28a745",
                                    border: "1px solid #baf0cc",
                                    borderRadius: "20px",
                                    padding: "4px 20px",
                                    cursor: isPaymentCleared || isPaying ? "not-allowed" : "pointer",
                                }}
                            >
                                {isPaying ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" />

                                    </>
                                ) : isPaymentRequired ? (
                                    "Pay Now"
                                ) : (
                                    "Paid"
                                )}
                            </button>
                        </div>

                        {!allPrechecksCompleted && (
                            <p style={{ color: "#dc3545", fontSize: "12px", marginTop: "10px", textAlign: "center" }}>
                                ⚠️ Admit card cannot be downloaded until all prechecks are completed.
                                Pending:{" "}
                                {studentData?.all_precheck_names
                                    ?.filter(name => !studentData?.completed_precheck_names?.includes(name))
                                    .join(", ")}
                            </p>
                        )}
                    </div>
                </>
            )}

            {/* {otpVerified && convenienceFee === 0 && ledgerBalance >= 0 && allPrechecksCompleted &&
                activityMeta?.display_download_admit_card_button === false && (
                <div
                    style={{
                        border: "1.5px dashed #f59e0b",
                        borderRadius: "10px",
                        padding: "16px 20px",
                        marginTop: "18px",
                        textAlign: "center",
                        background: "#fffbeb",
                    }}
                >
                    <div style={{ fontSize: "28px", marginBottom: "8px" }}>⏳</div>
                    <p style={{ margin: 0, fontWeight: 600, color: "#92400e", fontSize: "15px" }}>
                        Your Admit Card is Being Processed
                    </p>
                    <p style={{ margin: "6px 0 0", color: "#b45309", fontSize: "13px" }}>
                        All requirements are completed. Your admit card will be available shortly — please check back in a little while.
                    </p>
                </div>
            )} */}

            {otpVerified && canDownloadAdmitCard && (
                <div
                    style={{
                        border: "1.5px dashed #7b61ff",
                        borderRadius: "10px",
                        padding: "14px",
                        marginTop: "18px",
                        textAlign: "center",
                    }}
                >
                    <button
                        style={{
                            padding: "10px 18px",
                            borderRadius: "25px",
                            border: "1px solid #7F56DA",
                            background: "#7F56DA",
                            color: "white",
                            cursor: "pointer",
                        }}
                        onClick={handleDownloadAdmitCard}
                    >
                        {isDownloading ? (
                            <>
                                <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                />
                                {/* Downloading... */}
                            </>
                        ) : (
                            <>
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                                    height="20" width="20">
                                    <title>Download Admit Card</title>
                                    <g id="File / File_Download"
                                        stroke="#ffffff"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round">
                                        <g stroke="#ffffff" stroke-width="2.5">
                                            <path d="M12 12V18M12 18L15 16M12 18L9 16M13 3.00087C12.9045 3 12.7973 3 12.6747 3H8.2002C7.08009 3 6.51962 3 6.0918 3.21799C5.71547 3.40973 5.40973 3.71547 5.21799 4.0918C5 4.51962 5 5.08009 5 6.2002V17.8002C5 18.9203 5 19.4801 5.21799 19.9079C5.40973 20.2842 5.71547 20.5905 6.0918 20.7822C6.5192 21 7.07899 21 8.19691 21H15.8031C16.921 21 17.48 21 17.9074 20.7822C18.2837 20.5905 18.5905 20.2842 18.7822 19.9079C19 19.4805 19 18.9215 19 17.8036V9.32568C19 9.20296 19 9.09561 18.9991 9M13 3.00087C13.2856 3.00347 13.4663 3.01385 13.6388 3.05526C13.8429 3.10425 14.0379 3.18526 14.2168 3.29492C14.4186 3.41857 14.5918 3.59182 14.9375 3.9375L18.063 7.06298C18.4089 7.40889 18.5809 7.58136 18.7046 7.78319C18.8142 7.96214 18.8953 8.15726 18.9443 8.36133C18.9857 8.53376 18.9963 8.71451 18.9991 9M13 3.00087V5.8C13 6.9201 13 7.47977 13.218 7.90759C13.4097 8.28392 13.7155 8.59048 14.0918 8.78223C14.5192 9 15.079 9 16.1969 9H18.9991M18.9991 9H19.0002" />
                                        </g>

                                    </g>
                                </svg>Download Admit Card
                            </>)}
                    </button>
                </div>
            )}
        </div>
    );
};

export default StudentSelfAdmitCardGenerations;
